// AI-CONTEXT: See docs/features/platform-revenue-churn-trend.md
import formatMonthLabel from '../formatMonthLabel'
import { getSectorInfo } from '../meetstandaard/sectorCatalog'

// Restgroepen naast de echte sectoren. Dezelfde sleutels als
// functions/platformRevenue/splitAcrossSectors.js — bewaakt door de
// pariteitstest in buildSectorSeriesRows.test.js.
export const NO_SECTOR = 'geen-sector'
export const UNATTRIBUTED = 'niet-gekoppeld'

const RESTGROEPEN = [NO_SECTOR, UNATTRIBUTED]

const RESTGROEP_LABELS = {
    [NO_SECTOR]: 'Geen sector',
    [UNATTRIBUTED]: 'Stripe, niet gekoppeld',
}

/**
 * @param {string} slug
 * @returns {string}
 */
export const sectorSeriesLabel = (slug) => RESTGROEP_LABELS[slug] || getSectorInfo(slug).label

/**
 * @typedef {object} SectorPoint
 * @property {string} period - 'YYYY-MM'
 * @property {number|null} total
 * @property {Object<string, number>|null} bySector - null = deze maand is niet uitgesplitst
 */

/**
 * @typedef {object} SectorSeries
 * @property {Array<object>} rows - één rij per maand: { period, label, total, [sector]: number|null }
 * @property {Array<string>} sectors - sector-slugs, grootste eerst, restgroepen achteraan
 * @property {string|null} sectorsSince - eerste maand met een uitsplitsing
 */

/**
 * Maandpunten → rijen voor een recharts-grafiek, totaal en per sector.
 *
 * Gedeeld door de omzet- en de churnpagina: dezelfde vorm, dezelfde regel voor
 * wat 'onbekend' is.
 *
 * Een maand zonder uitsplitsing (van vóór de nachtrun hem bijhield) geeft per
 * sector null — een gat in de lijn. Een 0 zou beweren dat die sector toen niets
 * opbracht. Binnen een uitgesplitste maand is een ontbrekende sector wél 0: de
 * uitsplitsing is volledig.
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 *
 * @param {Array<SectorPoint>} points
 * @param {object} [options]
 * @param {(value: number) => number} [options.scale] - bijv. centen → euro
 * @returns {SectorSeries}
 */
const buildSectorSeriesRows = (points, { scale = (v) => v } = {}) => {
    const sorted = [...(Array.isArray(points) ? points : [])]
        .filter((p) => p && typeof p.period === 'string')
        .sort((a, b) => (a.period < b.period ? -1 : a.period > b.period ? 1 : 0))

    // Grootte per sector over de hele reeks, voor de volgorde in de legenda.
    const somPerSector = new Map()
    sorted.forEach((p) => {
        if (!p.bySector) return
        Object.entries(p.bySector).forEach(([slug, waarde]) => {
            somPerSector.set(slug, (somPerSector.get(slug) || 0) + (Number(waarde) || 0))
        })
    })

    const sectors = [...somPerSector.keys()].sort((a, b) => {
        const restA = RESTGROEPEN.indexOf(a)
        const restB = RESTGROEPEN.indexOf(b)
        if (restA !== restB) {
            if (restA === -1) return -1
            if (restB === -1) return 1
            return restA - restB
        }
        return somPerSector.get(b) - somPerSector.get(a) || a.localeCompare(b)
    })

    const rows = sorted.map((p) => {
        const row = {
            period: p.period,
            label: formatMonthLabel(p.period),
            total: Number.isFinite(p.total) ? scale(p.total) : null,
        }
        sectors.forEach((slug) => {
            row[slug] = p.bySector ? scale(Number(p.bySector[slug]) || 0) : null
        })
        return row
    })

    const eerste = sorted.find((p) => p.bySector)

    return { rows, sectors, sectorsSince: eerste ? eerste.period : null }
}

export default buildSectorSeriesRows
